import Vue from 'vue'
import axios from 'axios'
import router from '@/router'
import qs from 'qs'
import * as Sentry from '@sentry/browser'

const instance = axios.create({
  baseURL: `${process.env.VUE_APP_UOA_URL}/UnitOwnerAssembly`
})
instance.defaults.paramsSerializer = params => {
  return qs.stringify(params, { arrayFormat: 'repeat' })
}
instance.interceptors.request.use(
  reqConfig => {
    if (Vue.prototype.$getCookie('comLoginToken')) {
      const xCorrelationId = Vue.prototype.$getGuid()
      reqConfig.headers = {
        authorization: 'Bearer ' + Vue.prototype.$getCookie('comLoginToken').split('token=').pop(),
        authenticationscheme: 'SmartlifeBackstageAuthenticationHandler',
        'X-Correlation-Id': xCorrelationId
      }
      Sentry.setTag('X-Correlation-Id', xCorrelationId)
      return reqConfig
    } else {
      Vue.prototype.$alert('登入過期或驗證失敗，請重新登入', '警告', {
        confirmButtonText: '確定',
        type: 'warning',
        showClose: false,
        roundButton: true,
        closeOnClickModal: false
      }).then(() => {
        router.push({ name: 'LoginMain' })
      })
    }
  },
  err => {
    return Promise.reject(err)
  }
)
instance.interceptors.response.use(
  res => res,
  err => {
    console.log(err.response)
    // eslint-disable-next-line eqeqeq
    if (err && err.response && (err.response.status == 401 || err.response.status == 403 || err.response.status == 500)) {
      // eslint-disable-next-line eqeqeq
      if ((err.response.status == 401 || err.response.status == 403) && document.getElementsByClassName('v-modal').length == 0) {
        Vue.prototype.$alert(`${err.response.data.Message}`, '警告', {
          confirmButtonText: '確定',
          type: 'warning',
          showClose: false,
          roundButton: true,
          closeOnClickModal: false
        }).then(() => {
          // eslint-disable-next-line eqeqeq
          if (err.response.status == 401) { router.push({ name: 'LoginMain' }) } else if (err.response.status == 403) { Vue.prototype.$toMainPage() }
        })
        return Promise.reject(err)
      }
      // eslint-disable-next-line eqeqeq
      if (err.response.status == 500) { Vue.prototype.$message.error('發生未知錯誤，請稍後再試。') }
    } else {
      return Promise.reject(err)
    }
  }
)

// Swagger: https://uoa-dev.kingnetsmart.com.tw/swagger/index.html
export const apiUnitOwnerAssembly = {
  // 取得區權會會議列表
  GetAssemblyList: (requestData, config) => instance.get('/GetAssemblyList', Object.assign({}, { params: requestData }, config)),
  // 取得出席列表
  GetUnitOwnerList: (requestData, config) => instance.get('/GetUnitOwnerList', Object.assign({}, { params: requestData }, config)),
  // 匯出出席列表
  GetRegistrationExport: (requestData, config) => instance.get('/GetRegistrationExport', Object.assign({}, { params: requestData }, config)),
  // 取得DashboardData
  GetDashboardData: (requestData, config) => instance.get('/GetDashboardData', Object.assign({}, { params: requestData }, config)),
  // 會議相關
  Assembly: {
    GetAssembly: (requestData, config) => instance.get('/GetAssembly', Object.assign({}, { params: requestData }, config)),
    CreateAssembly: (requestData, config) => instance.post('/CreateAssembly', requestData, config),
    ImportUnitOwners: (requestData, config) => instance.post('/ImportUnitOwners', requestData, config),
    PublishAssembly: (requestData, config) => instance.patch('/PublishAssembly', requestData, config),
    StartAssembly: (requestData, config) => instance.patch('/StartAssembly', requestData, config),
    EditAssembly: (requestData, config) => instance.put('/EditAssembly', requestData, config),
    CancelAssembly: (queryPath, config) => instance.put('/CancelAssembly', config),
    DeleteAssembly: (config) => instance.delete('/DeleteAssembly', config),
    GetImportationTemplate: (config) => instance.get('/GetImportationTemplate', config),
    ResetAssembly: (config) => instance.delete('/ResetAssembly', config)
  },
  // 議案相關
  Topic: {
    GetTopicList: (requestData, config) => instance.get('/GetTopicList', Object.assign({}, { params: requestData }, config)),
    GetTopic: (requestData, config) => instance.get('/GetTopic', Object.assign({}, { params: requestData }, config)), // 取得單一議案資料
    InsertTopics: (requestData, config) => instance.post('/InsertTopics', requestData, config), // 覆蓋（更新全部）議案列表
    DeleteTopic: (requestData, config) => instance.delete('/DeleteTopic', requestData, config), // 刪除議案
    UpdateTopic: (requestData, config) => instance.put('/UpdateTopic', requestData, config), // 編輯議案
    CreateTopic: (requestData, config) => instance.post('/CreateTopic', requestData, config), // 新增臨時議案
    StartVoting: (requestData, config) => instance.post('/StartVoting', requestData, config), // 議案開始投票，選擇議案表決方式
    CloseVoting: (requestData, config) => instance.post('/CloseVoting', requestData, config), // 議案結束投票
    ResetVoting: (requestData, config) => instance.post('/ResetVoting', requestData, config) // 議案重新投票
  },
  // 投票相關
  Vote: {
    GetVotingDashboardData: (requestData, config) => instance.get('/GetVotingDashboardData', Object.assign({}, { params: requestData }, config)), // 取得議案投票
    GetVotes: (requestData, config) => instance.get('/GetVotes', Object.assign({}, { params: requestData }, config)), // 取得議案投票記名投票資料
    GetAssemblyVotingResultExport: (requestData, config) => instance.get('/GetAssemblyVotingResultExport', Object.assign({}, { params: requestData }, config)), // 取得總議案投票結果Excel
    SetVotes: (requestData, config) => instance.post('/SetVotes', requestData, config), // 送出不記名投票
    MakeDecision: (requestData, config) => instance.post('/MakeDecision', requestData, config), // 送出記名投票
    CancelDecision: (requestData, config) => instance.post('/CancelDecision', requestData, config), // 記名取消投票
    GetVoteInfo: (requestData, config) => instance.get('/GetVoteInfo', Object.assign({}, { params: requestData }, config)) // 取得住戶個別投票資訊
  },
  // 報到相關
  Register: {
    RegisterUnitOwner: (requestData, config) => instance.patch('/RegisterUnitOwner', requestData, config), // 本人報到
    RegisterAgent: (requestData, config) => instance.patch('/RegisterAgent', requestData, config), // 委託人報到
    UnregisterUnitOwner: (requestData, config) => instance.patch('/UnregisterUnitOwner', requestData, config), // 本人 or 委託人簽退
    GetUnitOwnerCardByTabletId: (requestData, config) => instance.get('/GetUnitOwnerCardByTabletId', Object.assign({}, { params: requestData }, config)),
    GetUnitOwnerCardListByMultipleWays: (requestData, config) => instance.get('/GetUnitOwnerCardListByMultipleWays', Object.assign({}, { params: requestData }, config)), // 使用載具/智失活會員條/會議條碼 進行報到或簽退
    CancelUnitOwnerRegistration: (requestData, config) => instance.patch('/CancelUnitOwnerRegistration', requestData, config),
    CancelUnitOwnerUnregistration: (requestData, config) => instance.patch('/CancelUnitOwnerUnregistration', requestData, config)
  },
  // 區權戶相關
  UnitOwner: {
    ModifyUnitOwners: (requestData, config) => instance.patch('/ModifyUnitOwners', requestData, config), // 新增、編輯、刪除 區權戶
    AddUnitOwner: (requestData, config) => instance.put('/AddUnitOwner', requestData, config) // 新增區權戶
  },
  // 列印相關
  Print: {
    GetAttendanceCardList: (requestData, config) => instance.get('/GetAttendanceCardList', Object.assign({}, { params: requestData }, config))
  }
}
